/**
 * OrderReviewSection — the Summary step's "Your order" box.
 *
 * Legacy renders this in the review column: the selected products with their
 * options and prices, each product's own notices, and the order's comments
 * (`Validation.controller` → `ProductListing onlySelected disabledSelection`).
 * On a v2 route it was suppressed, and deliberately so: that listing reads the
 * legacy `selectedProducts` map, which is keyed by numeric product ids the v2
 * cart has never carried, so it rendered an empty "Your order" box
 * (CCs-2 / W2.12). The cart sidebar showed the items, the review column did not.
 *
 * This is the native equivalent, built from the same `buildCartDisplay` the
 * sidebar uses so the two can never disagree about what is in the cart, plus the
 * per-product notices legacy shows in preview mode.
 *
 * WHAT PORTS, AND WHAT DOES NOT — the v2 catalogue is a smaller wire than v1's:
 * `CatalogProductV2DTO` carries `productCode / category / kind / documentSubtype
 * / area / price / addons / dependents / bundledProducts / requiredConfiguration
 * / inputImagePolicy` and nothing else (verified against /api-docs/v2).
 *
 *   - Floor-plan disclaimer — legacy tests `product.type === FLOOR_PLAN`, and
 *     `type` is a BE-computed bucket (`deriveProductTypeBucket`, from
 *     classification flags) that the v2 wire does not expose. Keyed on `kind`
 *     here against an explicit set. The measurements variant of the copy is
 *     exact (MEASUREMENT_ON_SITE is its own kind); the shooting variant depends
 *     on that set matching the BE's FLOOR_PLAN bucket, which is worth
 *     confirming — see DF-S1.
 *   - Region warning (Kampfmittelauskunft in a restricted German region) — ported
 *     by product CODE. Legacy matches a hardcoded numeric product id, which the
 *     v2 wire has no equivalent for; the dev product table gives the code
 *     (`products.product_id = 4258` → `KAMPFMITTELAUSKUNFT`), so the same rule
 *     applies here without inventing anything. See DF-S1 for what the data says
 *     about *which* product should carry this warning — it disagrees with legacy.
 *   - Measurement notices port exactly: both are keyed on the
 *     MEASUREMENT_ON_SITE kind and the same feature flag.
 */

import React from 'react'
import Big from 'big.js'
import { Box, Stack, Typography } from '@mui/material'
import { Trans, useTranslation } from 'react-i18next'
import WarningAmberOutlinedIcon from '@mui/icons-material/WarningAmberOutlined'

import { BEIGE_400, BEIGE_600, GRAY_700, GRAY_900 } from 'constants/styling/theme'
import { KampfmittelauskunftProductCodes, ProductKind } from 'constants/product'
import { RestrictedRegions } from 'constants/country/regions'
import type { GermanyRegionCodes } from 'constants/country/regions'
import { SectionedBorderBox } from 'components/common/SectionedBorderBox'
import { SystemMessage } from 'components/common/SystemMessage'
import { MeasurementInfoCard } from 'components/common/MeasurementInfoCard'
import { MeasurementReviewInfo } from 'components/common/MeasurementReviewInfo'
import { DynamicSummary, SummaryTextBlock } from 'components/common/OrderSummaryRows'

import type { CartItemAiEditing } from 'models/purchaseFlowRevamp'
import type { LandRegistrationSheetDTO } from 'models/purchaseFlow/AuthoritiesDocumentDTO'
import { formatPrice } from 'utils/price/priceFormatter'

import type { CartDisplayRow, CartExtraRow } from '../../components/cart/useCartDisplay'
import { StruckPrice } from '../../components/StruckPrice.component'
import type { ShootOutputUse } from '../../store/shootOutput'

/**
 * Kinds that live under legacy's FLOOR_PLAN type bucket and therefore carry its
 * disclaimer. MEASUREMENT_ON_SITE is handled separately — it selects the other
 * wording. FLOOR_PLAN_CERTIFICATION (living area calculation) is left out: it delivers a
 * document, not a plan in the floor-plan gallery (QA-506). See the file header on why this
 * is a set here and a BE field there.
 */
const FLOOR_PLAN_KINDS: ReadonlySet<string> = new Set<string>([
  ProductKind.MOBILE_APPLICATION_FLOOR_PLAN,
  ProductKind.FLOOR_PLAN_EDITING_2D,
  ProductKind.FLOOR_PLAN_EDITING_3D,
  ProductKind.FLOOR_PLAN_RENDERING_3D,
  ProductKind.MATTERPORT_FLOOR_PLAN_WITH_VISIT,
  ProductKind.MATTERPORT_FLOOR_PLAN_WITHOUT_VISIT,
])

/** One reviewed line: the cart row plus what the catalogue says about it. */
export interface OrderReviewLine extends CartDisplayRow {
  /** Wire `kind` of the chosen variant, when the catalogue resolved it. */
  kind?: string
  /** Wire `productCode` of the chosen variant — some notices key on the product
   *  itself rather than its kind. */
  productCode?: string
  /** Kinds of the add-ons this line selected — legacy suppresses the floor-plan
   *  disclaimer when 3D editing is among them, because the 3D deliverable is not
   *  what the disclaimer warns about. */
  selectedOptionKinds: string[]
  /** The client's own note on this line (`CartItem.note`). */
  note?: string
  /** The AI-editing settings the line carries, when its product collects them. */
  aiEditing?: CartItemAiEditing
  /** The files attached to this line, by the client's name, with a preview when the file is a visual. */
  uploads?: ReviewUpload[]
  /** The staging rooms set up for this line, one per uploaded photo. */
  stagingRooms?: ReviewStagingRoom[]
  /** The line is made from the order's shoot, so it lists no uploads or rooms (`shootOutputLines`). */
  fromShoot?: ShootOutputUse
  /** The land-register sheets typed for a document, when the client did not leave the lookup to Backbone. */
  landRegisterSheets?: LandRegistrationSheetDTO[]
  /** The floor-plan settings the line is drawn with, when it has a floor-plan configuration. */
  floorPlan?: ReviewFloorPlan
  /** The pack purchase this line belongs to; its lines are listed together under the pack's name. */
  pack?: { selectionId: string; name: string }
}

export interface ReviewUpload {
  name: string
  /** Web-size preview; absent for documents and for files not loaded in this session. */
  url?: string
}

/** What the client chose for one staging room, reduced to what the review lists. */
export interface ReviewStagingRoom {
  key: string
  url?: string
  /** True when the client left the room to Backbone. */
  backboneDecides: boolean
  roomTypes: string[]
  style?: string
  /** The objects to remove from the photo; absent when nothing is removed. */
  removal?: string
  decorations: number
  wall: boolean
  floor: boolean
}

export interface ReviewFloorPlan {
  is3D: boolean
  /** A colour in 2D, a style in 3D. */
  style?: string
  propertyType?: string | null
  comment?: string
}

/** A small square preview of an uploaded photo; a blank tile while its URL is not known. */
const ReviewThumbnail: React.FC<{ url?: string; alt?: string }> = ({ url, alt = '' }) => (
  <Box
    component={url ? 'img' : 'div'}
    src={url}
    alt={alt}
    sx={{ width: '4.8rem', height: '4.8rem', flexShrink: 0, borderRadius: '0.6rem', objectFit: 'cover', backgroundColor: BEIGE_400 }}
  />
)

/** One staging room's choices as a line, in the order the Configure step asks for them. */
const stagingRoomSettings = (room: ReviewStagingRoom, t: (key: string, options?: object) => string): string => {
  if (room.backboneDecides) return t('style_selection_order_step:staging_type.BKBN')
  return [
    room.style && `${t('style_selection_order_step:room_items.FURNITURE')}: ${t(`virtual_furniture_style_category:${room.style}`)}`,
    room.removal && t('purchase_flow_revamp:summary.staging_removal', { value: t(`virtual_furniture_room_removable_option:${room.removal}`) }),
    room.decorations > 0 && t('purchase_flow_revamp:summary.staging_decorations', { count: room.decorations }),
    room.wall && t('style_selection_order_step:room_items.WALL'),
    room.floor && t('style_selection_order_step:room_items.FLOOR'),
  ].filter(Boolean).join(' · ')
}

/** One sheet as a line: title number, district and parcels, leaving out what was not filled in. */
const sheetSummary = (sheet: LandRegistrationSheetDTO, label: (key: string) => string): string => {
  const parcels = sheet.floors.flatMap((floor) => floor.parcels.map((parcel) => parcel.number)).filter(Boolean)
  return [
    sheet.name && `${label('input_land_register_sheet')} ${sheet.name}`,
    sheet.district && `${label('input_district')} ${sheet.district}`,
    parcels.length > 0 && `${label('input_floor_parcel')} ${parcels.join(', ')}`,
  ].filter(Boolean).join(' · ')
}

interface Props {
  lines: OrderReviewLine[]
  /**
   * Charges that belong to the ORDER rather than to any one line: the client's
   * ticked extras, the selected access method's own fee, and the favourite
   * creative's travel cost. They are inside the total either way, so leaving
   * them off the box made the review disagree with the price being paid.
   */
  extras: CartExtraRow[]
  /** Region of the property being ordered for (`PropertyRef.regionCode`), which
   *  is what makes an authorities document restricted. */
  regionCode?: string
  /** Order-level comments (`comments` on the store), shown as legacy does. */
  comments?: string
  /** Formats a line's price exactly as the cart sidebar formats it. */
  formatLinePrice: (row: CartDisplayRow) => string
  /** The line's price without its saving, or null when it carries none. Optional so a
   *  hand-built caller (tests, storyshots) need not supply one. */
  formatBasePrice?: (row: CartDisplayRow) => string | null
  /** Same, for an order-level extra — one cart cannot show two prices. */
  formatExtraPrice: (extra: CartExtraRow) => string
  /** The extra's price without its saving, or null when it carries none. Optional like `formatBasePrice`. */
  formatExtraBasePrice?: (extra: CartExtraRow) => string | null
}

export const OrderReviewSection: React.FC<Props> = ({ lines, extras, regionCode, comments, formatLinePrice, formatBasePrice, formatExtraPrice, formatExtraBasePrice }) => {
  const { t } = useTranslation(['order', 'purchase_flow_revamp', 'ai_settings', 'purchase_flow', 'style_selection_order_step', 'virtual_furniture_room_type', 'virtual_furniture_style_category', 'virtual_furniture_room_removable_option'])
  const sheetLabel = (key: string) => t(`purchase_flow:property_information_step.land_register_sheet.${key}`)

  const isRegionRestricted = RestrictedRegions.has(regionCode as GermanyRegionCodes)

  if (lines.length === 0) return null

  const hasMeasurement = lines.some((line) => line.kind === ProductKind.MEASUREMENT_ON_SITE)

  const packTotals = new Map<string, string | null>()
  for (const line of lines) {
    if (!line.pack || packTotals.has(line.pack.selectionId)) continue
    const members = lines.filter((other) => other.pack?.selectionId === line.pack!.selectionId)
    const priced = members.every((member) => member.lineTotal && member.currency)
    const total = members.reduce((sum, member) => sum.plus(member.lineTotal ?? 0), new Big(0))
    packTotals.set(line.pack.selectionId, priced ? formatPrice(total, members[0].currency!) : null)
  }

  return (
    <SectionedBorderBox title={t('order:step_validation.your_order')}>
      <Stack gap={2} data-cy="pf-revamp-order-review">

        {lines.map((line, index) => {
          const startsPack = !!line.pack && lines[index - 1]?.pack?.selectionId !== line.pack.selectionId
          const isMeasurement = line.kind === ProductKind.MEASUREMENT_ON_SITE
          const isFloorPlan = isMeasurement || (!!line.kind && FLOOR_PLAN_KINDS.has(line.kind))
          const has3D = line.selectedOptionKinds.includes(ProductKind.FLOOR_PLAN_EDITING_3D) || !!line.floorPlan?.is3D
          const floorPlanSettings = line.floorPlan && [
            line.floorPlan.is3D ? '3D' : '2D',
            // An organization template carries a free-text style, so an unknown one is shown as it is, not as a raw key.
            line.floorPlan.style && t(`purchase_flow:floor_plan_step.design.${line.floorPlan.is3D ? 'styles' : 'colors'}.${line.floorPlan.style}`, { defaultValue: line.floorPlan.style }),
            line.floorPlan.propertyType && t(`purchase_flow:floor_plan_step.design.${line.floorPlan.propertyType.toLowerCase()}`),
          ].filter(Boolean).join(' · ')
          const disclaimerKey = isFloorPlan && !has3D
            ? (isMeasurement ? 'order:step_validation.floor_plan_disclaimer_measurements' : 'order:step_validation.floor_plan_disclaimer_shooting')
            : undefined

          return (
            <React.Fragment key={line.id}>
            {startsPack &&
              <Stack direction="row" justifyContent="space-between" gap={1} data-cy="pf-revamp-review-pack">
                <Typography variant="text-sm" fontWeight={600} color={GRAY_900}>{line.pack!.name}</Typography>
                {packTotals.get(line.pack!.selectionId) &&
                  <Typography variant="text-sm" fontWeight={600} color={GRAY_900} sx={{ whiteSpace: 'nowrap' }}>
                    {packTotals.get(line.pack!.selectionId)}
                  </Typography>
                }
              </Stack>
            }
            <Stack gap={1} sx={line.pack ? { pl: 1.2, borderLeft: `2px solid ${BEIGE_600}` } : undefined}>

              <Stack direction="row" justifyContent="space-between" gap={1} alignItems="flex-start">

                <Stack gap={0.2} flex={1} minWidth={0}>

                  <Typography variant="text-sm" fontWeight={550} color={GRAY_900}>
                    {line.quantity > 1 ? `${line.quantity} × ${line.shellDisplayName}` : line.shellDisplayName}
                  </Typography>

                  {!!line.variantDisplayName &&
                    <Typography variant="text-xs" color={GRAY_700}>
                      {line.variantDisplayName}
                    </Typography>
                  }

                  {line.aiEditing?.skyReplacement && !!line.aiEditing.cloudType &&
                    <Typography variant="text-xs" color={GRAY_700} data-cy="pf-revamp-review-sky">
                      {`${t('ai_settings:sky_replacement_title')}: ${t(`ai_settings:sky_replacement_options.${line.aiEditing.cloudType}`)}`}
                    </Typography>
                  }

                </Stack>

                <Typography variant="text-sm" fontWeight={550} color={GRAY_900} sx={{ whiteSpace: 'nowrap' }}>
                  <StruckPrice price={formatLinePrice(line)} base={formatBasePrice?.(line)} variant="text-sm" free={Boolean(line.lineTotal?.eq(0) && line.baseLineTotal)} />
                </Typography>

              </Stack>

              {/* Each add-on and extra by name and price, as the cart shows them: the line price includes them. */}
              {(line.addonLines ?? []).length > 0 &&
                <Stack gap={0.2} data-cy="pf-revamp-review-addons">
                  {line.addonLines.map(({ label, amount }) => (
                    <Stack key={label} direction="row" justifyContent="space-between" gap={0.8}>
                      <Typography variant="text-xs" color={GRAY_700}>{`+ ${label}`}</Typography>
                      {amount && line.currency &&
                        <Typography variant="text-xs" color={GRAY_700} whiteSpace="nowrap">
                          {formatPrice(amount, line.currency)}
                        </Typography>
                      }
                    </Stack>
                  ))}
                </Stack>
              }

              {!!floorPlanSettings &&
                <Typography variant="text-xs" color={GRAY_700} data-cy="pf-revamp-review-floor-plan">{floorPlanSettings}</Typography>
              }

              {!!line.floorPlan?.comment &&
                <SummaryTextBlock value={line.floorPlan.comment} />
              }

              {(line.landRegisterSheets ?? []).map((sheet, index) => {
                const summary = sheetSummary(sheet, sheetLabel)
                return summary
                  ? <Typography key={index} variant="text-xs" color={GRAY_700}>{summary}</Typography>
                  : null
              })}

              {!!line.fromShoot &&
                <Typography variant="text-xs" color={GRAY_700} data-cy="pf-revamp-review-from-shoot">
                  {t(`purchase_flow_revamp:summary.from_shoot.${line.fromShoot}`, { count: line.quantity })}
                </Typography>
              }

              {(line.stagingRooms ?? []).length > 0 &&
                <Stack gap={0.8} data-cy="pf-revamp-review-staging">
                  {line.stagingRooms!.map((room) => (
                    <Stack key={room.key} direction="row" gap={1} alignItems="center">
                      <ReviewThumbnail url={room.url} />
                      <Stack gap={0.2} minWidth={0}>
                        <Typography variant="text-xs" fontWeight={550} color={GRAY_900}>
                          {room.roomTypes.map((roomType) => t(`virtual_furniture_room_type:${roomType}`)).join(', ')}
                        </Typography>
                        <Typography variant="text-xs" color={GRAY_700}>
                          {stagingRoomSettings(room, t)}
                        </Typography>
                      </Stack>
                    </Stack>
                  ))}
                </Stack>
              }

              {(line.uploads ?? []).length > 0 &&
                <Stack gap={0.6} data-cy="pf-revamp-review-uploads">
                  <Typography variant="text-xs" fontWeight={550} color={GRAY_700}>
                    {`${t('purchase_flow_revamp:summary.uploaded_files')} (${line.uploads!.length})`}
                  </Typography>
                  {line.uploads!.some((upload) => upload.url) &&
                    <Stack direction="row" gap={0.6} flexWrap="wrap">
                      {line.uploads!.filter((upload) => upload.url).map((upload) => (
                        <ReviewThumbnail key={upload.name} url={upload.url} alt={upload.name} />
                      ))}
                    </Stack>
                  }
                  {line.uploads!.filter((upload) => !upload.url).map((upload) => (
                    <Typography key={upload.name} variant="text-xs" color={GRAY_700} noWrap>{upload.name}</Typography>
                  ))}
                </Stack>
              }

              {/* Legacy shows this on the product step; on review it is the last
                  chance to see it before paying for a document the authority may
                  not serve in this region. */}
              {isRegionRestricted && KampfmittelauskunftProductCodes.has(line.productCode ?? '') &&
                <SystemMessage
                  // Already-translated text, so raw: `SystemMessage` otherwise
                  // re-parses it as markup, and a bare `<` in any locale's copy
                  // would silently truncate the rest.
                  isRawContent
                  message={t('order:step_product.region_warning')}
                  variant="warning"
                />
              }

              {/* The client's own note on this product — legacy shows the order's
                  comments here; a per-line note is v2's finer-grained version. */}
              {!!line.note &&
                <SummaryTextBlock value={line.note} />
              }

              {/* Cost-calculation notice as an info card, matching the redesigned
                  measurement flow (ISSUE-3041); the shooting disclaimer and the
                  pre-redesign measurement copy keep the warning banner. */}
              {isMeasurement &&
                <MeasurementInfoCard
                  icon={<WarningAmberOutlinedIcon sx={{ fontSize: '1.8rem' }} />}
                  title={t('order:step_validation.cost_calculation_notice_title')}
                >
                  <Typography variant="text-sm" color={GRAY_900}>
                    {t('order:step_validation.cost_calculation_notice_text')}
                  </Typography>
                </MeasurementInfoCard>
              }

              {!!disclaimerKey && !isMeasurement &&
                <SystemMessage
                  variant="warning"
                  // `isRawContent` because the message is already a rendered
                  // `<Trans>`: without it `SystemMessage` wraps it in a SECOND
                  // Trans, which serialises its children and re-parses them as
                  // markup — the hazard its own prop doc describes. Legacy nests
                  // them; we do not.
                  isRawContent
                  message={
                    <Trans
                      t={t}
                      i18nKey={disclaimerKey}
                      components={{ list: <ul />, item: <li />, bold: <strong /> }}
                    >
                      &nbsp;
                    </Trans>
                  }
                />
              }

            </Stack>
            </React.Fragment>
          )
        })}

        {/* The order-level charges. Rendered after the products
            because that is the reading order of the total they add up to, and
            each one names itself so the box reconciles with the amount charged
            instead of showing an unexplained difference. */}
        {extras.map((extra) => (
          <Stack
            key={extra.kind}
            direction="row"
            justifyContent="space-between"
            gap={1}
            alignItems="flex-start"
            data-cy="pf-revamp-order-review-extra"
          >

            {/* Named like a product line, so the charge reads as one (QA-654). `defaultValue` mirrors
                `ExtrasSection` and the cart rail: a kind whose copy is not in the locale files yet shows its
                kind, never the raw i18n key. */}
            <Typography variant="text-sm" fontWeight={550} color={GRAY_900}>
              {t(extra.labelKey, { defaultValue: extra.kind, count: extra.count })}
            </Typography>

            <Typography variant="text-sm" fontWeight={550} color={GRAY_900} sx={{ whiteSpace: 'nowrap' }}>
              <StruckPrice price={formatExtraPrice(extra)} base={formatExtraBasePrice?.(extra)} variant="text-sm" />
            </Typography>

          </Stack>
        ))}

        {/* Technician call, delivery time and cancellation policy — legacy shows
            these under the listing for a measurement order (ISSUE-2189). */}
        {hasMeasurement &&
          <MeasurementReviewInfo />
        }

        {!!comments &&
          <DynamicSummary
            title={t('order:step_validation.product_comments')}
            value={<SummaryTextBlock value={comments} />}
          />
        }

      </Stack>
    </SectionedBorderBox>
  )
}
